import { IconCaretDownFilled, IconCaretUpFilled } from '@tabler/icons-react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import React, { useState, useEffect } from 'react';
import { CardHeader, Collapse } from 'reactstrap';
import classNames from 'classnames';
import { NSCard } from '../../bricks';
import RenderIf from '../RenderIf/RenderIf';

export interface IExpandableSectionProps {
    number?: number;
    title: string;
    subtitle: string;
    children: any;
    isExpanded?: boolean;
    className?: string;
    animate?: boolean;
    displaySectionButtons?: boolean;
    sectionButtons?: JSX.Element | null;
    customToggle?: React.ReactNode;
    onCustomToggle?: () => void;
    warningIcon?: React.ReactNode;
    showWarningIcon?: boolean;
    disableExpandingInReadOnly?: boolean;
}

export const ExpandableSection = ({
    number,
    title,
    subtitle,
    children,
    isExpanded = false,
    className = '',
    animate = false,
    displaySectionButtons = false,
    sectionButtons = null,
    customToggle,
    onCustomToggle,
    warningIcon,
    showWarningIcon,
    disableExpandingInReadOnly = false,
}: IExpandableSectionProps) => {
    const [isExpandedState, setExpandedState] = useState(isExpanded);

    const toggleExpanded = () => {
        setExpandedState(!isExpandedState);
        if (onCustomToggle) {
            onCustomToggle();
        }
    };

    useEffect(() => {
        setExpandedState(isExpanded);
    }, [isExpanded]);

    return (
        <NSCard className={`${className} shadow-none`}>
            <CardHeader>
                <div
                    className={classNames('d-flex align-items-center justify-content-between cursor--pointer text-white',
                        { 'ReadOnlyWrapper--enable-pointer-events': !disableExpandingInReadOnly })}
                    role="button"
                    tabIndex={0}
                >
                    <div className="d-flex align-items-center">
                        <RenderIf isTrue={number}>
                            <h4 className="pl-0 pr-3 mb-0 text-muted">{number}</h4>
                        </RenderIf>
                        <div>
                            <h4 className="mb-0 d-flex align-items-center">
                                {title}
                                <RenderIf isTrue={showWarningIcon}>
                                    <span className="ml-2">{warningIcon}</span>
                                </RenderIf>
                            </h4>
                            <h5 className="mb-1 mt-1 text-muted">{subtitle}</h5>
                        </div>
                    </div>
                    <div className="d-flex">
                        <RenderIf isTrue={displaySectionButtons}>
                            {sectionButtons}
                        </RenderIf>
                        <RenderIf isTrue={customToggle}>
                            <div role="presentation" onClick={toggleExpanded}>
                                {customToggle}
                            </div>
                        </RenderIf>
                        <RenderIf isTrue={!customToggle}>
                            <NSIcon
                                data-testid="expandIcon"
                                className="icon my-auto"
                                onClick={toggleExpanded}
                                icon={isExpandedState ? IconCaretUpFilled : IconCaretDownFilled}
                                size="lg"
                            />
                        </RenderIf>
                    </div>
                </div>
            </CardHeader>
            <Collapse isOpen={isExpandedState} enter={animate} exit={animate}>
                <RenderIf isTrue={isExpandedState}>
                    {children}
                </RenderIf>
            </Collapse>
        </NSCard>
    );
};

export default ExpandableSection;
